<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="modelValue" class="centered-modal-overlay" :class="{ 'centered-modal--light': isLightTheme }" :style="zIndex ? { zIndex } : {}" @click.self="handleClose">
        <div class="centered-modal" :style="{ maxWidth: maxWidth }">
          <button v-if="showCloseButton" class="close-button" @click="handleClose">
            <v-icon size="24">mdi-close</v-icon>
          </button>
          <div class="modal-content">
            <slot></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useTheme } from 'vuetify'

const theme = useTheme()
const isLightTheme = computed(() => !theme.current.value.dark)

defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  maxWidth: {
    type: String,
    default: '400px'
  },
  closeOnOverlay: {
    type: Boolean,
    default: true
  },
  zIndex: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['update:modelValue', 'close']);

const handleClose = () => {
  emit('update:modelValue', false);
  emit('close');
};
</script>

<style lang="scss" scoped>
.centered-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 9999;

  // Provide Vuetify theme vars for Teleported content (outside .v-application)
  --v-theme-sheetText: 255, 255, 255;
  --v-theme-sheetSection: 44, 44, 46;
  --v-theme-primary: 30, 136, 229;
}

.centered-modal {
  position: relative;
  width: 90%;
  padding: 40px 32px;
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(var(--v-terciary-base-rgb), 0.85);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  box-shadow: 
    0 8px 60px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

// Light mode overrides
.centered-modal--light {
  // Provide Vuetify theme vars for Teleported content (outside .v-application)
  --v-theme-sheetText: 0, 0, 0;
  --v-theme-sheetSection: 255, 255, 255;
  --v-theme-primary: 25, 118, 210;

  .centered-modal {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(0, 0, 0, 0.08);
    box-shadow:
      0 8px 60px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .close-button {
    border-color: rgba(0, 0, 0, 0.1);
    background: rgba(0, 0, 0, 0.06);
    color: #333;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .centered-modal,
.modal-fade-leave-active .centered-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .centered-modal {
  transform: scale(0.95);
  opacity: 0;
}

.modal-fade-leave-to .centered-modal {
  transform: scale(0.95);
  opacity: 0;
}
</style>
